/**
 * Inline settings body for one Automation — rendered beneath its card when that card is the open
 * one in the single-open accordion (Automations v2 §1). Replaces the former right-side
 * CustomizePanel drawer: the SAME settings, with the overlay chrome (backdrop, role=dialog/
 * aria-modal, focus trap, click-outside) dropped, and the §2 reorg folded in — the inbox-watcher
 * "What it can do" capabilities render ABOVE Save, the redundant "Draft replies" toggle is hidden,
 * and each capability save unions in `email.send` (the responder's always-on core function). The
 * disclosure a11y (aria-expanded/-controls, focus move, Escape-to-collapse) plus the on/off toggle
 * live on the card wrapper (AutomationCard) — this body carries no close ✕ or Status toggle of its own.
 */
import { useEffect, useState } from "react";
import type { CSSProperties } from "react";
import type { AutomationPreferences } from "@xavior/schema";
import type { Automation, PresetKind } from "./redesign/fixtures";
import {
  useSetAutomationPreferences,
  useSetAutomationCapabilities,
} from "./redesign/useAutomationsUi";
import { PRESET_META } from "./preset-meta";
import { PreferenceForm } from "./preference-forms";
import { ActionCapabilityToggles } from "@/common/blocks/ActionCapabilityToggles";
import { withResponderCoreActions } from "@/common/utils/action-vocabulary";
import {
  automationPrefsDraftKey,
  clearAutomationPrefsDraft,
  readAutomationPrefsDraft,
  writeAutomationPrefsDraft,
} from "./automationsSessionKeys";

export function AutomationSettings({
  kind,
  automation,
  moduleId,
}: {
  kind: PresetKind;
  automation: Automation | undefined;
  /** The owning module pane's instance id — scopes the unsaved-edit draft stash (sessionStorage)
   *  per module instance. Undefined disables the stash (behaves as before, #5571). The panel is
   *  headerless in this design — the owning AutomationCard's chevron (onCustomize, incl. Escape)
   *  owns collapse, so no in-panel onClose affordance is needed here. */
  moduleId?: string;
}) {
  const meta = PRESET_META[kind];
  const savePrefs = useSetAutomationPreferences();
  const saveCapabilities = useSetAutomationCapabilities();
  const id = automation?.id;
  // Cross-object capability toggles only apply to the reactive inbox responder. Default off.
  const showCapabilities = kind === "inbox_watcher";
  const allowlist = automation?.actionAllowlist ?? [];

  // The chat module pane unmounts the WHOLE module on switch-away (no keep-alive), which used to
  // silently discard an in-progress edit here with no warning (#5571). `draftKey` scopes an
  // unsaved-edit stash per module instance + preset kind; a null key (no moduleId yet) disables it.
  const draftKey = moduleId ? automationPrefsDraftKey(moduleId, kind) : null;

  // Local, unsaved preference edits — seeded from a stashed draft first (an edit left in progress
  // before this settings body was last unmounted), falling back to the automation's stored
  // preferences. Synchronous lazy initializer: the very first render after a remount already shows
  // the in-progress edit, no post-paint correction.
  const stored = automation?.preferences ?? {};
  const [localPrefs, setLocalPrefs] = useState<AutomationPreferences>(
    () => readAutomationPrefsDraft(draftKey) ?? stored,
  );
  // Re-seed when switching to a different automation (not on every poll, so in-flight edits
  // stick) — also consults the draft stash, so an automation that resolves AFTER this settings
  // body mounted (cold open before the roster fetch settles) still recovers its stashed edit.
  useEffect(() => {
    setLocalPrefs(readAutomationPrefsDraft(draftKey) ?? (automation?.preferences ?? {}));
  }, [automation?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = JSON.stringify(localPrefs) !== JSON.stringify(stored);

  // Write-back the in-progress edit on every change so a module-pane switch (full unmount) can't
  // silently drop it; clear once there's no unsaved diff left to protect (matches the fresh
  // stored value already, whether because nothing was ever typed or because a save round-tripped).
  useEffect(() => {
    if (dirty) writeAutomationPrefsDraft(draftKey, localPrefs);
    else clearAutomationPrefsDraft(draftKey);
  }, [draftKey, dirty, localPrefs]);

  return (
    <div
      className="autocard__settings"
      style={{ "--accent": meta.accent } as CSSProperties}
    >
      {/* Cross-object capability opt-in (reactive responder only) — default off, rendered ABOVE
          Save (Automations v2 §2). Each toggle writes the mapped action types into `actionAllowlist`
          via the config PATCH (validated server-side), saved immediately. The `reply` group is
          HIDDEN and `withResponderCoreActions` unions `email.send` into every saved list so the
          reduced UI can never clear the responder's core send capability. */}
      {showCapabilities && (
        <section className="customize__prefs" aria-label="What it can do">
          <div className="customize__rowlabel">What it can do</div>
          <div className="customize__rowhint">
            Off by default. Anything it proposes still waits for your sign-off
            in Review.
          </div>
          <ActionCapabilityToggles
            allowlist={allowlist}
            hiddenGroupIds={["reply"]}
            disabled={!id || saveCapabilities.isPending}
            onChange={(next) => {
              if (id)
                saveCapabilities.mutate({
                  id,
                  actionAllowlist: withResponderCoreActions(next),
                });
            }}
          />
        </section>
      )}

      {/* Per-kind preferences form — local edits, saved on the button below. */}
      <section className="customize__prefs" aria-label="Preferences">
        <PreferenceForm
          key={id ?? kind}
          kind={kind}
          value={localPrefs}
          onChange={setLocalPrefs}
        />
        <div className="customize__actions">
          <button
            type="button"
            className="customize__save"
            disabled={!id || !dirty || savePrefs.isPending}
            onClick={() => {
              if (id)
                savePrefs.mutate(
                  { id, preferences: localPrefs },
                  {
                    // Explicit, deterministic clear on success — the hook-level onSuccess only
                    // invalidates the list query, and that refetch landing (which is what makes
                    // `dirty` fall back to false on its own) isn't synchronous with this call.
                    onSuccess: () => clearAutomationPrefsDraft(draftKey),
                  },
                );
            }}
          >
            Save
          </button>
        </div>
      </section>
    </div>
  );
}
